// useEditGoals.js
//
// Drives the "Edit Goals" modal. Fetches the combined goals-management
// snapshot (current-week roll/wrap targets, all static production goals, and
// the current-year YTD monthly targets), and exposes both:
//   - the last confirmed server data (read-only display / "what's live now")
//   - an editable "draft" copy the modal's form inputs bind to
//
// Mirrors the fetch/cache/AbortController pattern used by
// useActivePersonnel.js, with added draft-state management for editing.
//
// ── Week navigation ───────────────────────────────────────────────────────────
//
// goToWeek(dateStr) lets the modal load/edit ANY week's roll & wrap targets.
// It is intentionally scoped to ONLY the weeklyRollGoals slice of state — it
// merges the new week's data into serverData/draft without touching
// productionGoals/ytdCompletionGoals, so navigating weeks never discards
// in-progress edits made elsewhere in the modal.
//
// ── lineLeader field ──────────────────────────────────────────────────────────
//
// lineLeader is a week-level field on weeklyRollGoals (not per-day).
// It lives on the weeklyRollGoals object because it is logically scoped to the
// week being edited, and navigating to a different week via goToWeek() replaces
// the entire weeklyRollGoals slice — including lineLeader — with the new week's
// data automatically, with no special-casing needed.
//
// updateLineLeader(value) mutates draft.weeklyRollGoals.lineLeader.
// The value is stored as a trimmed string or null (empty string → null) so the
// draft's lineLeader is always in a clean state before being sent to the POST.
//
// ── Save behaviour ────────────────────────────────────────────────────────────
//
// This hook owns READ and DRAFT state only — it does not make POST requests.
// Saving is handled entirely by useUpdateGoals (dataPoster.js). After a
// successful POST, editGoals.js calls commitDraft(result.updated) here to:
//   1. Merge the server-returned goalIds into the current draft (so freshly
//      INSERTed rows get a real goalId and lose their "New" tag).
//   2. Promote that merged draft as the new serverData baseline (clears isDirty).
//   3. Update the module-level cache so re-opens don't refetch unnecessarily.
//
// commitDraft does NOT need to merge lineLeader back from the server — the
// POST echoes nothing new for it (it is not a goalId). The draft's lineLeader
// value is already the correct committed value; it simply becomes serverData.

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { fetchGoalsManagement } from "../../utils/dataReceiver";

/**
 * @typedef {{
 *   weekStart:   string | null,
 *   weekEnd:     string | null,
 *   lineLeader:  string | null,
 *   days: Array<{
 *     date:       string,
 *     dayLabel:   string,
 *     roll:       number,
 *     rollGoalId: string | null,
 *     wrap:       number,
 *     wrapGoalId: string | null,
 *   }>,
 * }} WeeklyRollGoals
 *
 *
 * @typedef {{
 *   goalId:    string | null,
 *   goalName:  string,
 *   goalValue: number,
 *   unitType:  "numeric" | "percent" | "numeric-thousand",   ← NEW
 *   updatedAt: string | null,
 * }} ProductionGoal
 *
 *
 * @typedef {{
 *   year: number,
 *   months: Array<{
 *     month:     string,
 *     monthDate: string,
 *     goalValue: number,
 *     goalId:    string | null,
 *     hasGoal:   boolean,
 *   }>,
 * }} YtdCompletionGoals
 */

let _cache = null;
let _cacheTime = 0;
// Shorter TTL than useActivePersonnel's 60s — this hook backs an editable
// form, where serving slightly-stale data risks the user editing on top of
// numbers someone else already changed.
const CACHE_TTL_MS = 30_000;

const EMPTY_WEEKLY_ROLL_GOALS = {
  weekStart: null,
  weekEnd: null,
  lineLeader: null, // ← NEW
  days: [],
};
const EMPTY_YTD_COMPLETION_GOALS = {
  year: new Date().getUTCFullYear(),
  months: [],
};

const todayDateString = () => new Date().toISOString().slice(0, 10);

/** Coerce a form input value to a finite number, defaulting to 0. */
function toNumberOrZero(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Coerce a text input value to a trimmed string or null.
 * Empty / whitespace-only strings normalise to null so lineLeader is never
 * stored as "" in the draft — the POST and the DB receive null cleanly.
 */
function toStringOrNull(value) {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed || null;
}

/**
 * Fetch and manage editable state for the Edit Goals modal.
 *
 * @returns {{
 *   weeklyRollGoals:        WeeklyRollGoals,        last confirmed server data
 *   productionGoals:        ProductionGoal[],       last confirmed server data
 *   ytdCompletionGoals:     YtdCompletionGoals,     last confirmed server data
 *
 *   draftWeeklyRollGoals:    WeeklyRollGoals,        editable copy — bind inputs here
 *   draftProductionGoals:    ProductionGoal[],       editable copy — bind inputs here
 *   draftYtdCompletionGoals: YtdCompletionGoals,     editable copy — bind inputs here
 *
 *   loading:   boolean,        true during the initial combined load
 *   error:     string | null,
 *   isDirty:   boolean,        true when draft differs from server data
 *
 *   weekDate:    string,           "YYYY-MM-DD" — date currently driving the weekly view
 *   loadingWeek: boolean,          true while a week-navigation fetch is in flight
 *   weekError:   string | null,
 *   goToWeek:    (dateStr: string) => void,
 *
 *   updateWeeklyRollGoal: (date: string, type: "roll" | "wrap", value: number) => void,
 *   updateLineLeader:     (value: string) => void,   ← NEW
 *   updateProductionGoal: (goalId: string, value: number) => void,
 *   updateYtdGoal:        (monthDate: string, value: number) => void,
 *   resetDraft:           () => void,
 *   commitDraft:          (updated: UpdatedGoalIds) => void,
 *   refetch:              () => void,
 * }}
 */
const useEditGoals = () => {
  const [serverData, setServerData] = useState(_cache);
  const [draft, setDraft] = useState(_cache);
  const [loading, setLoading] = useState(!_cache);
  const [error, setError] = useState(null);
  const [weekDate, setWeekDate] = useState(todayDateString);
  const [loadingWeek, setLoadingWeek] = useState(false);
  const [weekError, setWeekError] = useState(null);
  const weekFetchControllerRef = useRef(null);

  // ── Core loader — the initial combined fetch (all 3 tables, today's week) ──
  const load = useCallback(async (signal, { bypassCache = false } = {}) => {
    const isStale =
      bypassCache || !_cache || Date.now() - _cacheTime > CACHE_TTL_MS;

    if (!isStale) {
      setServerData(_cache);
      setDraft(_cache);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await fetchGoalsManagement(signal);
      _cache = data;
      _cacheTime = Date.now();
      setServerData(data);
      setDraft(data);
    } catch (err) {
      if (err.name === "AbortError") return;
      setError(err.message ?? "Failed to load production goals.");
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Fire once on mount ───────────────────────────────────────────────────────
  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // Abort any in-flight week fetch on unmount.
  useEffect(() => {
    return () => weekFetchControllerRef.current?.abort();
  }, []);

  // ── Manual refetch — e.g. call when the modal re-opens ──────────────────────
  // Resets the week filter back to "today" so a re-opened modal always starts
  // from a known, current state.
  const refetch = useCallback(() => {
    setWeekDate(todayDateString());
    const controller = new AbortController();
    load(controller.signal, { bypassCache: true });
  }, [load]);

  // ── Week navigation — targeted fetch, merges weeklyRollGoals ONLY ───────────
  //
  // Deliberately does NOT go through the main `load()` path: that would
  // replace the entire serverData/draft objects, discarding any unsaved
  // production-goal / YTD edits the user made before switching weeks.
  // lineLeader is included in weeklyRollGoals and so is naturally replaced
  // when the new week's data lands — no special handling needed.
  const goToWeek = useCallback((dateStr) => {
    if (!dateStr) return;

    weekFetchControllerRef.current?.abort();
    const controller = new AbortController();
    weekFetchControllerRef.current = controller;

    setWeekDate(dateStr);
    setLoadingWeek(true);
    setWeekError(null);

    fetchGoalsManagement(controller.signal, { weekDate: dateStr })
      .then((data) => {
        setServerData((prev) =>
          prev ? { ...prev, weeklyRollGoals: data.weeklyRollGoals } : data,
        );
        setDraft((prev) =>
          prev ? { ...prev, weeklyRollGoals: data.weeklyRollGoals } : data,
        );
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setWeekError(err.message ?? "Failed to load that week's goals.");
      })
      .finally(() => {
        setLoadingWeek(false);
      });
  }, []);

  // ── Draft mutation helpers — form inputs call these on change ───────────────

  const updateWeeklyRollGoal = useCallback((date, type, value) => {
    const numericValue = toNumberOrZero(value);
    setDraft((prev) => {
      if (!prev) return prev;
      const days = prev.weeklyRollGoals.days.map((d) => {
        if (d.date !== date) return d;
        if (type === "roll") return { ...d, roll: numericValue };
        if (type === "wrap") return { ...d, wrap: numericValue };
        return d;
      });
      return { ...prev, weeklyRollGoals: { ...prev.weeklyRollGoals, days } };
    });
  }, []);

  // ── updateLineLeader — NEW ──────────────────────────────────────────────────
  //
  // Updates the week-level lineLeader on the draft's weeklyRollGoals object.
  // Storing it on weeklyRollGoals (not at the top-level draft) means it is
  // automatically cleared when goToWeek() replaces the weeklyRollGoals slice,
  // which is exactly the right behaviour — each week has its own leader.
  const updateLineLeader = useCallback((value) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        weeklyRollGoals: {
          ...prev.weeklyRollGoals,
          lineLeader: toStringOrNull(value),
        },
      };
    });
  }, []);

  const updateProductionGoal = useCallback((goalId, value) => {
    const numericValue = toNumberOrZero(value);
    setDraft((prev) => {
      if (!prev) return prev;
      const productionGoals = prev.productionGoals.map((g) =>
        g.goalId === goalId ? { ...g, goalValue: numericValue } : g,
      );
      return { ...prev, productionGoals };
    });
  }, []);

  const updateYtdGoal = useCallback((monthDate, value) => {
    const numericValue = toNumberOrZero(value);
    setDraft((prev) => {
      if (!prev) return prev;
      const months = prev.ytdCompletionGoals.months.map((m) =>
        m.monthDate === monthDate
          ? { ...m, goalValue: numericValue, hasGoal: true }
          : m,
      );
      return {
        ...prev,
        ytdCompletionGoals: { ...prev.ytdCompletionGoals, months },
      };
    });
  }, []);

  const resetDraft = useCallback(() => {
    setDraft(serverData);
  }, [serverData]);

  const isDirty = useMemo(() => {
    if (!serverData || !draft) return false;
    return JSON.stringify(serverData) !== JSON.stringify(draft);
  }, [serverData, draft]);

  // ── commitDraft — called by editGoals.js after a successful POST ────────────
  //
  // Merges server-returned goalIds back into the draft, then promotes the merged
  // draft as the new serverData baseline (clearing isDirty).
  //
  // lineLeader does NOT need to be merged from the POST response — the server
  // does not return a new goalId for it. The draft's lineLeader is already the
  // user's committed value; promoting the draft to serverData is sufficient.
  //
  // Shape of `updated` (from dataPoster.js):
  //   {
  //     weeklyRollGoals:    [{ date, goalType, goalId }, ...]
  //     productionGoals:    [{ goalName, goalId }, ...]
  //     ytdCompletionGoals: [{ monthDate, goalId }, ...]
  //   }
  const commitDraft = useCallback(
    (updated) => {
      if (!draft) return;

      // Build lookup maps for O(1) goalId access per row.
      const rollGoalIdByKey = new Map(
        (updated?.weeklyRollGoals ?? [])
          .filter((r) => r.goalType === "roll")
          .map((r) => [r.date, r.goalId]),
      );
      const wrapGoalIdByKey = new Map(
        (updated?.weeklyRollGoals ?? [])
          .filter((r) => r.goalType === "wrap")
          .map((r) => [r.date, r.goalId]),
      );
      const prodGoalIdByName = new Map(
        (updated?.productionGoals ?? []).map((r) => [r.goalName, r.goalId]),
      );
      const ytdGoalIdByMonth = new Map(
        (updated?.ytdCompletionGoals ?? []).map((r) => [r.monthDate, r.goalId]),
      );

      const mergedDraft = {
        ...draft,
        weeklyRollGoals: {
          ...draft.weeklyRollGoals,
          // lineLeader is already correct in the draft — no merge needed.
          days: draft.weeklyRollGoals.days.map((d) => ({
            ...d,
            rollGoalId: rollGoalIdByKey.get(d.date) ?? d.rollGoalId,
            wrapGoalId: wrapGoalIdByKey.get(d.date) ?? d.wrapGoalId,
          })),
        },
        productionGoals: draft.productionGoals.map((g) => ({
          ...g,
          goalId: prodGoalIdByName.get(g.goalName) ?? g.goalId,
        })),
        ytdCompletionGoals: {
          ...draft.ytdCompletionGoals,
          months: draft.ytdCompletionGoals.months.map((m) => ({
            ...m,
            goalId: ytdGoalIdByMonth.get(m.monthDate) ?? m.goalId,
            hasGoal: ytdGoalIdByMonth.has(m.monthDate)
              ? ytdGoalIdByMonth.get(m.monthDate) !== null
              : m.hasGoal,
          })),
        },
      };

      setDraft(mergedDraft);
      setServerData(mergedDraft);
      _cache = mergedDraft;
      _cacheTime = Date.now();
    },
    [draft],
  );

  return {
    weeklyRollGoals: serverData?.weeklyRollGoals ?? EMPTY_WEEKLY_ROLL_GOALS,
    productionGoals: serverData?.productionGoals ?? [],
    ytdCompletionGoals:
      serverData?.ytdCompletionGoals ?? EMPTY_YTD_COMPLETION_GOALS,

    draftWeeklyRollGoals: draft?.weeklyRollGoals ?? EMPTY_WEEKLY_ROLL_GOALS,
    draftProductionGoals: draft?.productionGoals ?? [],
    draftYtdCompletionGoals:
      draft?.ytdCompletionGoals ?? EMPTY_YTD_COMPLETION_GOALS,

    loading,
    error,
    isDirty,

    weekDate,
    loadingWeek,
    weekError,
    goToWeek,

    updateWeeklyRollGoal,
    updateLineLeader, // ← NEW
    updateProductionGoal,
    updateYtdGoal,
    resetDraft,
    commitDraft,
    refetch,
  };
};

export default useEditGoals;
